// app/src/features/timeline/renderers/renderContractBody.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import CopyLinksPanel from "../../../components/CopyLinksPanel.jsx";
import { DrawerHelp, HelpReopenButton } from "../../../components/Panels.jsx";
import QuestionDates from "../../../components/QuestionDates.jsx";
import { C } from "../../../lib/colors.js";
import { forecasterDebugLabel, rootEngine } from "../../../lib/forecast/engines.js";
import { answerSummary, answerText } from "../../../lib/forecast/answerSummary.js";
import { sideChanceText } from "../../../lib/forecast/formatPct.js";
import { describeResolutionMoment } from "../../../lib/formatDates.js";
import { recordUiEvent } from "../../../lib/logRingBuffer";
import { sourceNameOf } from "../../../lib/report/sourceLabel.js";
import { BRANCH_COLORS } from "../../../lib/timeline/geometry.js";
import { markDrawerHelpSeen } from "../../../onboarding/drawerHelp";

// The Question (contract) details: the classic top drawer's body, also shown in the rail's
// Question section and its popover. `stacked` lays the columns out one above another for a
// narrow panel.
export function renderContractBody(ctx, stacked) {
  const {
    active,
    branches,
    compareOn,
    compareRoots,
    contractOpen,
    copyLinks,
    copyLinksAvailable,
    copyLinksListExpanded,
    currentLink,
    displayActive,
    drawerHelpState,
    mintedStash,
    openSendCopy,
    setDrawerHelpState,
    toggleCopyLinksList,
  } = ctx;
  return (
    <>
      {contractOpen && !drawerHelpState.contract && (
        <DrawerHelp
          k="contract"
          surface="question"
          onClose={() => {
            markDrawerHelpSeen("contract");
            setDrawerHelpState((s) => ({ ...s, contract: true }));
          }}
        />
      )}
      {contractOpen && drawerHelpState.contract && (
        <HelpReopenButton
          corner="br"
          surface="question"
          onClick={() => setDrawerHelpState((s) => ({ ...s, contract: false }))}
        />
      )}
      {compareOn && compareRoots[1] ? (
        <div style={{ padding: "14px 20px", fontSize: 12 }}>
          <div style={{ fontSize: 12, color: C.gray, fontWeight: 500, marginBottom: 8 }}>
            Question
          </div>
          <div style={{ color: C.white, lineHeight: 1.6, marginBottom: 12 }}>
            {active?.contract?.question || (
              <span style={{ color: C.gray, fontStyle: "italic" }}>—</span>
            )}
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: stacked ? "1fr" : "1fr 1fr", gap: 16 }}
          >
            <div style={{ borderLeft: `2px solid ${C.purple}`, paddingLeft: 10 }}>
              <div style={{ fontSize: 11, color: C.purple, letterSpacing: 0.6, marginBottom: 4 }}>
                {forecasterDebugLabel(rootEngine(active)).toUpperCase()}
              </div>
              <div style={{ fontSize: 29, color: C.purple, fontWeight: "bold", marginBottom: 4 }}>
                {answerText(answerSummary(active)) ?? "–"}
              </div>
              <div style={{ fontSize: 12, color: C.white, marginBottom: 4 }}>
                {active?.contract?.title || ""}
              </div>
              <div style={{ fontSize: 11, color: C.grayL }}>
                Resolves {active?.contract?.resolution_date || "—"}
              </div>
              <div style={{ fontSize: 11, marginTop: 4 }}>
                {(active?.contract?.sources || []).length > 0 ? (
                  <span style={{ color: C.purple }}>
                    {active.contract.sources.map((src) => sourceNameOf(src) || src).join(" · ")}
                  </span>
                ) : (
                  <span style={{ color: C.gray, fontStyle: "italic" }}>—</span>
                )}
              </div>
            </div>
            <div style={{ borderLeft: `2px solid ${C.green}`, paddingLeft: 10 }}>
              <div style={{ fontSize: 11, color: C.green, letterSpacing: 0.6, marginBottom: 4 }}>
                {forecasterDebugLabel(rootEngine(compareRoots[1])).toUpperCase()}
              </div>
              <div style={{ fontSize: 29, color: C.green, fontWeight: "bold", marginBottom: 4 }}>
                {answerText(answerSummary(compareRoots[1])) ?? "–"}
              </div>
              <div style={{ fontSize: 12, color: C.white, marginBottom: 4 }}>
                {compareRoots[1].contract?.title || ""}
              </div>
              <div style={{ fontSize: 11, color: C.grayL }}>
                Resolves {compareRoots[1].contract?.resolution_date || "—"}
              </div>
              <div style={{ fontSize: 11, marginTop: 4 }}>
                {(compareRoots[1].contract?.sources || []).length > 0 ? (
                  <span style={{ color: C.green }}>
                    {compareRoots[1].contract.sources
                      .map((src) => sourceNameOf(src) || src)
                      .join(" · ")}
                  </span>
                ) : (
                  <span style={{ color: C.gray, fontStyle: "italic" }}>—</span>
                )}
              </div>
            </div>
          </div>
          {branches.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {branches.map((b, i) => {
                const bp = b.contract?.probability ?? null;
                return (
                  <div
                    key={b.runId}
                    style={{
                      color: BRANCH_COLORS[i % BRANCH_COLORS.length],
                      fontSize: 12,
                      marginTop: 3,
                    }}
                  >
                    ⤷ {b.modification}
                    {bp != null ? ` → ${sideChanceText(bp)}` : ""}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            padding: "14px 20px",
            display: "grid",
            gridTemplateColumns: stacked ? "1fr" : "1fr 1fr 1fr",
            gap: 16,
            fontSize: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                color: C.white,
                fontWeight: 600,
                marginBottom: 5,
              }}
            >
              Question
            </div>
            <div style={{ color: C.white, lineHeight: 1.6 }}>
              {displayActive?.contract?.question || (
                <span style={{ color: C.gray, fontStyle: "italic" }}>—</span>
              )}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                color: C.white,
                fontWeight: 600,
                marginBottom: 5,
              }}
            >
              How it resolves
            </div>
            <div style={{ color: C.grayL, lineHeight: 1.5 }}>
              {displayActive?.contract?.resolution_criteria || (
                <span style={{ color: C.gray, fontStyle: "italic" }}>—</span>
              )}
            </div>
            {/* The deadline the server actually resolved, and why it chose
                    that timezone. Shown so an inference is correctable: "by EOD
                    today" is read in the asker's calendar, and "market close" in
                    the exchange's, neither of which the reader can otherwise
                    see. Rendered only when the server recorded a basis, so an
                    older server's payload looks exactly as it did before. */}
            {displayActive?.contract?.resolution_basis &&
              describeResolutionMoment(
                displayActive.contract.resolution_moment,
                displayActive.contract.resolution_zone,
                displayActive.contract.resolution_basis,
              ) && (
                <div style={{ color: C.gray, fontSize: 12, marginTop: 4 }}>
                  Resolves{" "}
                  {describeResolutionMoment(
                    displayActive.contract.resolution_moment,
                    displayActive.contract.resolution_zone,
                    displayActive.contract.resolution_basis,
                    displayActive.contract.resolution_label,
                  )}
                </div>
              )}
            {branches.length > 0 && (
              <>
                {branches.map((b, i) => {
                  const bp = b.contract?.probability ?? null;
                  return (
                    <div
                      key={b.runId}
                      style={{
                        color: BRANCH_COLORS[i % BRANCH_COLORS.length],
                        fontSize: 12,
                        marginTop: 3,
                      }}
                    >
                      ⤷ {b.modification}
                      {bp != null ? ` → ${sideChanceText(bp)}` : ""}
                    </div>
                  );
                })}
              </>
            )}
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                color: C.white,
                fontWeight: 600,
                marginBottom: 5,
              }}
            >
              Background
            </div>
            <div style={{ color: C.grayL, lineHeight: 1.6 }}>
              {displayActive?.contract?.background || (
                <span style={{ color: C.gray, fontStyle: "italic" }}>—</span>
              )}
            </div>
          </div>
          <QuestionDates
            createdAt={active?.createdAt}
            resolutionDate={displayActive?.contract?.resolution_date}
          />
        </div>
      )}
      {/* COPY LINKS owner-side entry points. Rendered after the compare/normal fork so
             both layouts get them, and gated on `copyLinksAvailable` plus a persisted
             `forecastId`: an unsaved run has no server row to mint against, and `active.id`
             (the client_id) is NOT a substitute. The `/copy/:id` redeem route stays OUTSIDE
             the gates on purpose — a recipient must not need a debug switch to open a link
             someone sent them, and the server enforces the deployment gate there itself. */}
      {copyLinksAvailable && active?.forecastId && (
        <div data-copy-links-section="" style={{ padding: "0 20px 14px" }}>
          {/* The CREATE button lives inside the panel so the row it shares with the list's
                 collapse toggle is not split across files. */}
          <CopyLinksPanel
            links={copyLinks.links}
            liveCount={copyLinks.liveCount}
            loading={copyLinks.loading}
            revokingId={copyLinks.revokingId}
            error={copyLinks.error}
            availability={copyLinks.availability}
            availabilityDetail={copyLinks.availabilityDetail}
            // Thin cross-hook adapter: a successful revoke ERASES the stashed
            // URL for that link, because offering a URL that 410s is worse
            // than having lost it. `forgetIf` compares ids, so revoking a
            // different link leaves a still-good URL alone. A FAILED revoke
            // deliberately keeps it — a falsy Bigtable write means the
            // capability may still redeem.
            onRevoke={async (copyId) => {
              recordUiEvent({ event: "ui.copy_link.revoke", copyId });
              const ok = await copyLinks.revoke(copyId);
              if (!ok) recordUiEvent({ event: "ui.copy_link.revoke_failed", copyId });
              if (ok) mintedStash.forgetIf(copyId);
              return ok;
            }}
            onCreate={() => {
              // Opening the mint flow, not the mint itself — the modal can
              // be abandoned, and the difference matters when reading a
              // report about a link that "wasn't created".
              recordUiEvent({
                event: "ui.copy_link.create_open",
                forecastId: active?.forecastId || null,
              });
              openSendCopy();
            }}
            copyableCopyId={currentLink?.copyId ?? null}
            currentLink={currentLink}
            onCopyLink={() => {
              // The URL is NEVER recorded — it carries the capability in
              // its fragment, and a bug bundle is not a place to put one.
              recordUiEvent({
                event: "ui.copy_link.copied",
                copyId: currentLink?.copyId || null,
              });
              mintedStash.copy(currentLink?.url);
            }}
            justCopied={mintedStash.justCopied}
            copyFailed={mintedStash.copyFailed}
            expanded={copyLinksListExpanded}
            onToggleExpanded={toggleCopyLinksList}
          />
        </div>
      )}
    </>
  );
}
